import { useEffect, useRef } from "react";
import { animate, motion, useInView, useMotionValue, useTransform } from "motion/react";
import { SectionHeading } from "../ui/SectionHeading";
import { Reveal } from "../ui/Reveal";
import { Parallax } from "../ui/Parallax";
import { profile, stats } from "../../data/content";
import snehaPhoto from "../../assets/sneha-profile.jpg";

function Counter({ value, suffix }: { value: number; suffix: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const count = useMotionValue(0);
  const display = useTransform(count, (v) => `${Math.round(v)}`);

  useEffect(() => {
    if (!inView) return;
    const controls = animate(count, value, { duration: 2.2, ease: [0.22, 1, 0.36, 1] });
    return () => controls.stop();
  }, [inView, value, count]);

  return (
    <span ref={ref} className="font-display text-4xl font-bold text-white md:text-5xl">
      <motion.span>{display}</motion.span>
      <span className="text-gradient">{suffix}</span>
    </span>
  );
}

export function About() {
  const paragraphs = [
    "My journey started in graphic design, where I learned to see — typography, color, composition and the details that make visuals feel right. That foundation led me to instructional design at Tejas Networks, where I spent 1.5 years turning complex technical ideas into clear, engaging learning experiences.",
    "Today I'm a Software Engineer at Tejas Networks, combining software and web development with UI/UX design, graphic design and AI tools. I care about the same things in code that I cared about in design — clarity, craft and experiences that feel effortless.",
  ];

  return (
    <section id="about" className="section">
      <div className="mx-auto max-w-6xl px-6 md:px-10">
        <SectionHeading eyebrow="01 · About" title="From design to" accent="software engineering." />

        <div className="grid gap-14 lg:grid-cols-[0.9fr_1.1fr] lg:gap-20">
          {/* Visual */}
          <Reveal className="order-2 lg:order-1">
            <Parallax className="relative mx-auto max-w-sm lg:max-w-none" distance={36}>
              <div className="card-glow group relative aspect-[4/5] overflow-hidden rounded-[2rem] border border-white/10 bg-gradient-to-br from-surface to-ink-2 p-8">
                {/* gradient ring */}
                <div className="absolute inset-0 rounded-[2rem] bg-gradient-to-br from-primary/40 via-transparent to-accent/30 opacity-60" />
                <div className="absolute inset-px rounded-[calc(2rem-1px)] bg-[#0b0e1a]" />

                <div className="relative flex h-full flex-col justify-between">
                  <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.3em] text-muted">
                    <span>Portfolio</span>
                    <span>©2026</span>
                  </div>

                  <div className="flex flex-col items-center">
                    <div className="relative">
                      <div className="absolute -inset-6 rounded-full bg-primary/20 blur-2xl animate-pulse-glow" />
                      <div className="relative h-40 w-40 overflow-hidden rounded-full border border-white/10 bg-white/5 backdrop-blur">
                        <img
                          src={snehaPhoto}
                          alt={profile.name}
                          className="h-full w-full object-cover"
                        />
                      </div>
                    </div>
                    <p className="mt-6 font-display text-lg font-semibold text-white">
                      {profile.name}
                    </p>
                    <p className="font-mono text-xs uppercase tracking-[0.3em] text-muted">
                      {profile.role}
                    </p>
                  </div>

                  <div className="grid grid-cols-3 gap-2 text-center">
                    {["Design", "Instruction", "Code"].map((t) => (
                      <div
                        key={t}
                        className="rounded-xl border border-white/10 bg-white/5 py-2 font-mono text-[10px] uppercase tracking-widest text-white/70"
                      >
                        {t}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* floating badge */}
              <div className="absolute -right-4 -top-4 animate-float rounded-2xl border border-white/10 bg-surface/90 px-4 py-3 shadow-xl shadow-black/40 backdrop-blur">
                <p className="font-display text-2xl font-bold text-gradient">2+</p>
                <p className="font-mono text-[9px] uppercase tracking-widest text-muted">Years exp</p>
              </div>
            </Parallax>
          </Reveal>

          {/* Text */}
          <div className="order-1 lg:order-2">
            <Reveal>
              <h3 className="font-display text-2xl font-semibold leading-snug text-white md:text-3xl">
                Building products that feel{" "}
                <span className="text-gradient-animated">alive</span> — where every
                pixel has a purpose.
              </h3>
            </Reveal>

            <div className="mt-6 space-y-4">
              {paragraphs.map((p, i) => (
                <Reveal key={i} delay={0.1 + i * 0.1}>
                  <p className="leading-relaxed text-white/60">{p}</p>
                </Reveal>
              ))}
            </div>

            <Reveal delay={0.25}>
              <div className="mt-10 grid grid-cols-2 gap-6 sm:grid-cols-4">
                {stats.map((s) => (
                  <div key={s.label} className="border-l border-white/10 pl-4">
                    <Counter value={s.value} suffix={s.suffix} />
                    <p className="mt-1 text-sm text-muted">{s.label}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
